import {Double} from '../@types/util';
import {
  KpiAssignee,
  KpiAssigneeType,
  KpiAssignment,
  KpiAssignmentId,
  KpiReminder,
  KpiTarget,
  KpiTargetContinuity,
  KpiTargetFrequency,
  KpiTargetId,
  KpiTargetNumericType,
  KpiTargetTree,
} from '../@types/kpi';
import {List, Map} from 'immutable';
import {SkillId} from 'records/Skill';
import {javaApiFor} from 'sources/api';
import {AutocompleteResults} from '@components/auto-complete/SingleAutocomplete';
import {Moment} from 'moment';
import {UserId} from 'records/User';
import pairsToMap, {KotlinPair} from 'utils/pairsToMap';
import {TeamId} from 'records/TeamId';

// TODO update to include /api/ prefix
const kpiTargetApi = javaApiFor(`/profile/kpi_targets`);
const kpiAssignmentApi = javaApiFor(`/profile/kpi_assignments`);

export interface ModifyKpiRequest {
  name: string;
  numericType: KpiTargetNumericType;
  value: Double;
  frequency: KpiTargetFrequency;
  continuity: KpiTargetContinuity;
  childIds: KpiTargetId[];
  skillIds: SkillId[];
  chartAsSkill: boolean;
  chartAsEngagement: boolean;
}

export interface ModifyKpiAssignmentRequest {
  kpiId: KpiTargetId;
  assignee: {id: string; type: KpiAssigneeType};
  reminder: KpiReminder;
}

const dateFormat = 'YYYY-MM-DD';

/** @link KpiTargetActions
 *  @link KpiTargetStore */
export default class KpiTargetSource {
  static fetchGoalKpiTree(): Promise<List<KpiTargetTree>> {
    return kpiTargetApi
      .getJson('')
      .then(results => List(results.map(tree => new KpiTargetTree(tree))));
  }

  static fetchUsedByDateAndTeams(
    start: Moment,
    end: Moment,
    filter: TeamId[] | UserId
  ): Promise<KpiTarget[]> {
    const params = Array.isArray(filter) ? {team: filter} : {userId: filter};
    return kpiTargetApi
      .getJson('/assigned', {
        params: {
          start_date: start.format(dateFormat),
          end_date: end.format(dateFormat),
          ...params,
        },
      })
      .then(results => results.map(target => new KpiTarget(target)));
  }

  static create(params: ModifyKpiRequest): Promise<KpiTarget> {
    return kpiTargetApi.postJson<ModifyKpiRequest>('', params).then(t => new KpiTarget(t));
  }

  static delete(id: KpiTargetId): Promise<void> {
    return kpiTargetApi.delete(`/${id}`);
  }

  static update(id: KpiTargetId, params: ModifyKpiRequest): Promise<KpiTarget> {
    return kpiTargetApi.putJson<ModifyKpiRequest>(`/${id}`, params).then(t => new KpiTarget(t));
  }

  static allAssignments(): Promise<List<KpiAssignment>> {
    return kpiAssignmentApi
      .getJson('')
      .then(results => List(results.map(a => new KpiAssignment(a))));
  }

  static assigneeAutocomplete(search: string): Promise<KpiAssignee[]> {
    return kpiAssignmentApi.getJson('/autocomplete', {
      params: {
        search,
      },
    });
  }

  static assigneeAutocompleteCommon(): Promise<AutocompleteResults<KpiAssignee>> {
    return kpiAssignmentApi.getJson('/autocomplete/common');
  }

  static createAssignment(params: ModifyKpiAssignmentRequest): Promise<KpiAssignment> {
    return kpiAssignmentApi
      .postJson<ModifyKpiAssignmentRequest>('', params)
      .then(a => new KpiAssignment(a));
  }

  static updateAssignment(
    id: KpiAssignmentId,
    params: ModifyKpiAssignmentRequest
  ): Promise<KpiAssignment> {
    return kpiAssignmentApi
      .putJson<ModifyKpiAssignmentRequest>(`/${id}`, params)
      .then(a => new KpiAssignment(a));
  }

  static deleteAssignment(id: KpiAssignmentId): Promise<void> {
    return kpiAssignmentApi.delete(`/${id}`);
  }

  static forceCompleteAssignment(id: KpiAssignmentId): Promise<void> {
    return kpiAssignmentApi.put(`/${id}/complete`);
  }

  static completedAssignments(): Promise<Map<KpiAssignmentId, boolean>> {
    return kpiAssignmentApi
      .getJson<KotlinPair<KpiAssignmentId, boolean>[]>('/completed')
      .then(pairsToMap);
  }
}
